import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { Auth } from "aws-amplify";

/**
 * Spot Trade S3 Service for managing presigned URLs for spot trading CSV files
 * Uses Cognito credentials via AWS Amplify Auth
 */
export class SpotTradeS3Service {
  /**
   * Extract both bucket name and S3 key from presigned URL
   * @param {string} presignedUrl - Full presigned URL
   * @returns {{bucketName: string, s3Key: string}} Object containing bucket name and S3 key
   */
  extractBucketAndKeyFromUrl(presignedUrl) {
    try {
      const url = new URL(presignedUrl);
      let bucketName, s3Key;

      // Handle different S3 URL formats
      if (url.hostname.includes(".s3.") || url.hostname.includes(".s3-")) {
        // Virtual-hosted-style URL: https://bucket-name.s3.region.amazonaws.com/key
        // or https://bucket-name.s3-region.amazonaws.com/key
        bucketName = url.hostname.split(".")[0];
        s3Key = decodeURIComponent(url.pathname.substring(1));
      } else if (url.hostname.startsWith("s3.") || url.hostname.startsWith("s3-")) {
        // Path-style URL: https://s3.region.amazonaws.com/bucket-name/key
        // or https://s3-region.amazonaws.com/bucket-name/key
        const pathParts = url.pathname.substring(1).split("/");
        bucketName = pathParts[0];
        s3Key = decodeURIComponent(pathParts.slice(1).join("/"));
      } else {
        throw new Error("Unrecognized S3 URL format");
      }

      if (!bucketName || !s3Key) {
        throw new Error("Could not extract bucket name or S3 key from URL");
      }

      return { bucketName, s3Key };
    } catch (error) {
      console.error("Failed to extract bucket and key from URL:", error);
      throw new Error(`Invalid S3 URL format: ${error.message}`);
    }
  }

  /**
   * Generate new presigned URL for existing S3 object
   * @param {string} existingPresignedUrl - Current presigned URL
   * @returns {Promise<string>} New presigned URL with extended expiration
   */
  async regeneratePresignedUrl(existingPresignedUrl) {
    try {
      // Get current AWS credentials from Cognito via Amplify
      const credentials = await Auth.currentCredentials();

      // Create S3 client with Cognito credentials
      const s3Client = new S3Client({
        region: "us-west-2", // Same region as backend
        credentials: Auth.essentialCredentials(credentials),
      });

      // Extract both bucket name and S3 key from existing URL
      const { bucketName, s3Key } = this.extractBucketAndKeyFromUrl(existingPresignedUrl);

      console.log("Regenerating presigned URL for bucket:", bucketName, "key:", s3Key);

      // Generate new presigned URL (7 days expiration)
      const newPresignedUrl = await getSignedUrl(
        s3Client,
        new GetObjectCommand({
          Bucket: bucketName,
          Key: s3Key,
        }),
        { expiresIn: 7 * 24 * 60 * 60 } // 7 days
      );

      console.log("Successfully generated new presigned URL");
      return newPresignedUrl;
    } catch (error) {
      console.error("Failed to regenerate presigned URL:", error);
      throw new Error(`S3 URL regeneration failed: ${error.message}`);
    }
  }

  /**
   * Check if a presigned URL is expired or about to expire
   * @param {string} presignedUrl - Presigned URL to check
   * @returns {boolean} True if URL is expired or expires within 1 hour
   */
  isUrlExpiredOrExpiring(presignedUrl) {
    try {
      const url = new URL(presignedUrl);
      const expiresParam = url.searchParams.get("X-Amz-Expires");
      const dateParam = url.searchParams.get("X-Amz-Date");

      if (!expiresParam || !dateParam) {
        return true; // If we can't determine expiration, assume expired
      }

      // Parse X-Amz-Date (format: 20241201T100000Z)
      const year = dateParam.substring(0, 4);
      const month = dateParam.substring(4, 6);
      const day = dateParam.substring(6, 8);
      const hour = dateParam.substring(9, 11);
      const minute = dateParam.substring(11, 13);
      const second = dateParam.substring(13, 15);

      const signedDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
      const expirationDate = new Date(signedDate.getTime() + parseInt(expiresParam) * 1000);

      // Check if expires within 1 hour
      const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);

      return expirationDate <= oneHourFromNow;
    } catch (error) {
      console.error("Failed to check URL expiration:", error);
      return true; // If we can't parse, assume expired
    }
  }
}

const spotTradeS3Service = new SpotTradeS3Service();
export default spotTradeS3Service;
